/**
 * 메뉴 urlPath → 프론트 라우트 메타(아이콘·라벨).
 * menuKey는 서버 난수이므로 사이드바 연결은 urlPath만 사용한다.
 * 아이콘은 기존 admin/settings 사이드바 설정을 SSOT로 재사용한다.
 */
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { Menu, type LucideIcon } from 'lucide-react';
import {
  adminSidebarMenuItems,
  settingsSidebarMenuItems,
  type MenuGroup,
  type MenuLink,
  type SidebarMenuItem,
} from './sidebarMenuConfig';

export type MenuUrlRoute = {
  path: string;
  icon: LucideIcon;
  label: string;
  /** true면 sidebarMenuConfig(또는 별칭)에서 찾은 아이콘 */
  fromCatalog: boolean;
};

/** 설정 모드 전용 경로 — 메인 사이드바에서 제외, 설정 사이드바에서만 사용 */
export const SETTINGS_SIDEBAR_URLS = new Set<string>([
  ROUTE_PATHS.MY_ACCOUNT,
  ROUTE_PATHS.SETTINGS_ACCOUNTS,
  ROUTE_PATHS.SETTINGS_AUDIT_LOG,
  ROUTE_PATHS.SETTINGS_MENUS,
  ROUTE_PATHS.SETTINGS_PARTNERS,
]);

const isMenuGroup = (item: SidebarMenuItem): item is MenuGroup =>
  !('type' in item) && 'children' in item;

const isMenuLink = (item: SidebarMenuItem): item is MenuLink =>
  !('type' in item) && 'key' in item && !('children' in item);

/**
 * API urlPath 정규화 — 절대 URL·선행 슬래시·트레일링 슬래시 차이를 흡수한다.
 */
export const normalizeMenuUrlPath = (
  urlPath: string | null | undefined,
): string | null => {
  if (!urlPath?.trim()) return null;
  let path = urlPath.trim();
  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname;
    }
  } catch {
    // 잘못된 absolute URL이면 path 그대로 시도
  }
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1) path = path.replace(/\/+$/, '');
  return path;
};

/**
 * 사이드바에 없는 하위/관련 경로 → 목록 메뉴와 동일 아이콘·권한을 쓰기 위한 별칭.
 * (상세 `$id`는 longest-prefix로 부모 목록에 매칭)
 */
export const ROUTE_PERMISSION_ALIASES: Array<{
  path: string;
  from: string;
  label: string;
}> = [
  {
    path: ROUTE_PATHS.PHARMA_COMPANY_NEW,
    from: ROUTE_PATHS.PHARMA_COMPANIES,
    label: '제약사 등록',
  },
  {
    path: ROUTE_PATHS.PARTNER_COMPANY_NEW,
    from: ROUTE_PATHS.PARTNER_COMPANY,
    label: '업체 등록',
  },
  {
    path: ROUTE_PATHS.DRUG_DATA_CREATE,
    from: ROUTE_PATHS.DRUGS,
    label: '의약품 데이터 생성',
  },
  {
    path: ROUTE_PATHS.SETTLEMENT_GENERATOR,
    from: ROUTE_PATHS.SETTLEMENT_UPLOAD,
    label: '정산 시트 생성기',
  },
  {
    path: ROUTE_PATHS.COMMISSION_AGGREGATOR,
    from: ROUTE_PATHS.SETTLEMENT_FEES,
    label: '수수료 집계',
  },
  {
    path: ROUTE_PATHS.BOARD_ANNOUNCEMENT_NEW,
    from: ROUTE_PATHS.BOARD_ANNOUNCEMENTS,
    label: '공지 작성',
  },
  {
    path: ROUTE_PATHS.BASE_FEE_RATE_SETTING,
    from: ROUTE_PATHS.BASE_FEE_RATES,
    label: '기본 수수료율 설정',
  },
  {
    path: ROUTE_PATHS.DETAIL_FEE_RATE_SETTING,
    from: ROUTE_PATHS.DETAIL_FEE_RATES,
    label: '상세 수수료율 설정',
  },
];

/** @deprecated 아이콘 별칭 — ROUTE_PERMISSION_ALIASES와 동일 */
const ROUTE_ICON_ALIASES = ROUTE_PERMISSION_ALIASES;

const ingestSidebarItems = (
  items: SidebarMenuItem[],
  routeMap: Record<string, MenuUrlRoute>,
  groupIconByChildPath: Record<string, LucideIcon>,
  pathByMenuCode: Record<string, string>,
) => {
  for (const item of items) {
    if ('type' in item) continue;

    if (isMenuGroup(item)) {
      for (const child of item.children) {
        const path = normalizeMenuUrlPath(child.key) ?? child.key;
        routeMap[path] = {
          path,
          icon: child.icon,
          label: child.label,
          fromCatalog: true,
        };
        groupIconByChildPath[path] = item.icon;
        if (child.menuCode && !pathByMenuCode[child.menuCode]) {
          pathByMenuCode[child.menuCode] = path;
        }
      }
      continue;
    }

    if (isMenuLink(item)) {
      const path = normalizeMenuUrlPath(item.key) ?? item.key;
      routeMap[path] = {
        path,
        icon: item.icon,
        label: item.label,
        fromCatalog: true,
      };
      if (item.menuCode && !pathByMenuCode[item.menuCode]) {
        pathByMenuCode[item.menuCode] = path;
      }
    }
  }
};

const applyRouteIconAliases = (routeMap: Record<string, MenuUrlRoute>) => {
  for (const alias of ROUTE_ICON_ALIASES) {
    const aliasPath = normalizeMenuUrlPath(alias.path) ?? alias.path;
    if (routeMap[aliasPath]) continue;
    const fromPath = normalizeMenuUrlPath(alias.from) ?? alias.from;
    const source = routeMap[fromPath];
    if (!source) continue;
    routeMap[aliasPath] = {
      path: aliasPath,
      icon: source.icon,
      label: alias.label,
      fromCatalog: true,
    };
  }
};

/** 하드코딩 사이드바 경로 기준으로 urlPath → 아이콘/라벨·그룹 아이콘 맵 */
export const buildMenuUrlRouteMaps = (): {
  routeMap: Record<string, MenuUrlRoute>;
  groupIconByChildPath: Record<string, LucideIcon>;
  pathByMenuCode: Record<string, string>;
} => {
  const routeMap: Record<string, MenuUrlRoute> = {};
  const groupIconByChildPath: Record<string, LucideIcon> = {};
  const pathByMenuCode: Record<string, string> = {};

  ingestSidebarItems(
    adminSidebarMenuItems,
    routeMap,
    groupIconByChildPath,
    pathByMenuCode,
  );
  ingestSidebarItems(
    settingsSidebarMenuItems,
    routeMap,
    groupIconByChildPath,
    pathByMenuCode,
  );
  applyRouteIconAliases(routeMap);

  return { routeMap, groupIconByChildPath, pathByMenuCode };
};

const built = buildMenuUrlRouteMaps();

export const MENU_URL_ROUTE_MAP = built.routeMap;

/** 자식 urlPath → 기존 사이드바 그룹 아이콘 (Database, Users, …) */
export const MENU_GROUP_ICON_BY_CHILD_PATH = built.groupIconByChildPath;

/** 카탈로그 menuCode → 대표 urlPath (`/admin/auth/me` urlPath 폴백) */
export const CATALOG_PATH_BY_MENU_CODE = built.pathByMenuCode;

const CATALOG_PATHS_BY_LENGTH = Object.keys(MENU_URL_ROUTE_MAP).sort(
  (a, b) => b.length - a.length,
);

export const isSettingsSidebarUrl = (
  urlPath: string | null | undefined,
): boolean => {
  const path = normalizeMenuUrlPath(urlPath);
  return Boolean(path && SETTINGS_SIDEBAR_URLS.has(path));
};

/**
 * urlPath → 라우트 메타.
 * 1) 정규화 후 정확 일치 2) 가장 긴 접두 일치(상세 경로 등)
 * 카탈로그에 없으면 fromCatalog:false + Menu 아이콘(표시용 폴백).
 */
export const resolveRouteByUrlPath = (
  urlPath: string | null | undefined,
): MenuUrlRoute | null => {
  const path = normalizeMenuUrlPath(urlPath);
  if (!path) return null;

  const exact = MENU_URL_ROUTE_MAP[path];
  if (exact) return { ...exact, path };

  for (const catalogPath of CATALOG_PATHS_BY_LENGTH) {
    if (path === catalogPath || path.startsWith(`${catalogPath}/`)) {
      const hit = MENU_URL_ROUTE_MAP[catalogPath];
      if (!hit) continue;
      // 네비게이션은 API가 준 path를 유지하고, 아이콘·라벨만 카탈로그에서 빌린다
      return {
        path,
        icon: hit.icon,
        label: hit.label,
        fromCatalog: true,
      };
    }
  }

  return {
    path,
    icon: Menu,
    label: path,
    fromCatalog: false,
  };
};

const resolveGroupIconForPath = (childPath: string): LucideIcon | undefined => {
  const normalized = normalizeMenuUrlPath(childPath);
  if (!normalized) return undefined;
  if (MENU_GROUP_ICON_BY_CHILD_PATH[normalized]) {
    return MENU_GROUP_ICON_BY_CHILD_PATH[normalized];
  }
  for (const catalogPath of CATALOG_PATHS_BY_LENGTH) {
    if (
      normalized === catalogPath ||
      normalized.startsWith(`${catalogPath}/`)
    ) {
      const groupIcon = MENU_GROUP_ICON_BY_CHILD_PATH[catalogPath];
      if (groupIcon) return groupIcon;
    }
  }
  return undefined;
};

/**
 * 그룹 메뉴 아이콘 — 자체 url이 있으면 그 아이콘,
 * 없으면 자식 url이 속한 기존 사이드바 그룹 아이콘을 사용한다.
 */
export const resolveGroupIcon = (
  groupUrlPath: string | null | undefined,
  childPaths: string[],
): LucideIcon => {
  const self = resolveRouteByUrlPath(groupUrlPath);
  if (self?.fromCatalog) return self.icon;

  for (const path of childPaths) {
    const groupIcon = resolveGroupIconForPath(path);
    if (groupIcon) return groupIcon;
  }

  for (const path of childPaths) {
    const leaf = resolveRouteByUrlPath(path);
    if (leaf?.fromCatalog) return leaf.icon;
  }

  return Menu;
};
